// Dependencies
import React from 'react';
import { connect } from 'react-redux';
import { Box, Text } from '@nerdwallet/currency';
import DTITooHighModal from '../../dti-too-high-modal/dti-too-high-modal';
import CityHeader from '../../results-panel/city-header/city-header';
import DTISlider from '../../results-panel/dti-slider/dti-slider';
import HomeValue from '../../currency/home-value/home-value';
import MonthlyPayment from '../../monthly-payment/monthly-payment';

// CSS
import styles from './results-panel.module.less';
import formatOutput from './format-output';
import Breakdown from '../../breakdown/breakdown';
import {
  showInsufficientDisclaimer,
  showMinimumDisclaimer,
} from '../../monthly-payment/utils';
import { HOME_AFFORDABILITY } from '../../../lib/analytics-constants';
import SegmentImpression, {
  useSegmentImpressionContext,
} from '../../../lib/analytics/segment-impression';
import { formatResultForSegment } from '../../../lib/analytics/format-result-for-segment';

interface ResultsPanelProps {
  data: any;
  onSectionEnter?: (index: number) => void;
  className?: string;
  creditScoreCtaConfig?: any;
  DTIDetailsConfig: any;
  onChange: any;
  browser: {
    isMobile: boolean;
    isTablet: boolean;
  };
}

interface OwnProps extends ResultsPanelProps {
  isInsufficientDisclaimerVisible: boolean;
  isMinimumDisclaimerVisible: boolean;
}

const ResultsPanel = ({
  onSectionEnter,
  className,
  creditScoreCtaConfig,
  DTIDetailsConfig,
  browser,
  data,
  onChange,
  isInsufficientDisclaimerVisible,
  isMinimumDisclaimerVisible,
}: OwnProps): JSX.Element => {
  const childProps = {
    browser,
    data,
    onChange,
    creditScoreCtaConfig,
  };
  const minimumDisclaimer =
    'Note: Down payment used is the minimum down payment for your loan type.';
  const insufficientDisclaimer =
    'Note: The "Down payment & closing costs" you added weren\'t sufficient to cover closing costs, so these closing costs and affordability assume you do. You can also roll the VA funding fee into original mortgage balance.';
  const content = [
    {
      label: 'Monthly payment',
      content: <MonthlyPayment calculator="homeAffordability" />,
    },
    {
      label: 'Down payment and closing costs',
      content: (
        <Box>
          <MonthlyPayment calculator="homeAffordability" view="DownPayments" />
          {isInsufficientDisclaimerVisible ? (
            <Box padding={3}>
              <Text size="small">{insufficientDisclaimer}</Text>
            </Box>
          ) : null}
          {isMinimumDisclaimerVisible ? (
            <Box padding={3}>
              <Text size="small">{minimumDisclaimer}</Text>
            </Box>
          ) : null}
        </Box>
      ),
    },
  ];

  const [homeValueStr, suggestedPrice, monthlyPayment, dti] =
    formatOutput(data);
  const { eventId: formViewedId } = useSegmentImpressionContext() || {};

  return (
    <Box className={styles['results-panel']}>
      <DTITooHighModal {...childProps} />
      <CityHeader {...childProps} browser={browser} />
      <SegmentImpression
        eventType="feature_viewed"
        attributes={{
          entity_name: 'form_result_text',
          section_name: 'form',
          item_position: null,
          item_copy: 'how_much_house_can_i_afford_calculator',
          content_id: null,
          additional_attributes: formatResultForSegment([
            {
              label: 'Your selected home price',
              displayValue: homeValueStr,
            },
            {
              label: 'Suggested Home Price',
              displayValue: suggestedPrice,
            },
            {
              label: 'Your Monthly Payment',
              displayValue: monthlyPayment,
            },
          ]),
          parent_event_id: formViewedId,
        }}
      >
        <HomeValue
          formattedHomeValue={homeValueStr}
          formattedSuggestedPrice={suggestedPrice}
        />
      </SegmentImpression>
      <SegmentImpression
        eventType="feature_viewed"
        attributes={{
          entity_name: 'form_result_text',
          section_name: 'form',
          item_position: null,
          item_copy: 'how_much_house_can_i_afford_calculator',
          content_id: null,
          additional_attributes: formatResultForSegment([
            {
              label: 'debt-to-income ratio (DTI)',
              displayValue: dti,
            },
          ]),
          parent_event_id: formViewedId,
        }}
      >
        <DTISlider
          {...childProps}
          browser={browser}
          DTIDetailsConfig={DTIDetailsConfig}
        />
      </SegmentImpression>
      <Breakdown
        className={className}
        content={content}
        onSectionEnter={onSectionEnter}
      />
    </Box>
  );
};

ResultsPanel.displayName = 'ResultsPanel';

ResultsPanel.defaultProps = {
  creditScoreCtaConfig: {},
};

const mapStateToProps = (state) => {
  return {
    isInsufficientDisclaimerVisible: showInsufficientDisclaimer(
      state,
      HOME_AFFORDABILITY
    ),
    isMinimumDisclaimerVisible: showMinimumDisclaimer(
      state,
      HOME_AFFORDABILITY
    ),
  };
};

export default connect(mapStateToProps)(ResultsPanel);
